import type { ConnectionSummary, DatasetFacts } from '@/shared/api/generated/catalog';
import type { RuleBinding } from '@/shared/api/generated/rules';

/* The catalogue drill-down's presentation decisions, as functions rather than JSX.
 *
 * Everything here is a judgement about what a figure *means*, which is why it is testable without
 * rendering anything. The recurring one, at every level: a number this platform did not measure must
 * not be shown as a number it did.
 */

/** Whether a source's catalogue is currently being rebuilt, and how far along.
 *
 * Its own type rather than a pair of numbers because the *reason* to show progress is that a harvest
 * is running — `discovered` and `observed` are stale figures from the last finished run otherwise,
 * and rendering `8 of 8` beside a source nobody is harvesting reads as a live measurement.
 */
export type HarvestActivity =
  | { readonly kind: 'idle' }
  | { readonly kind: 'running'; readonly observed: number; readonly discovered: number }
  /* Running with no `discovered` yet. A harvest reports its total once it has listed the source, so
   * the first seconds of a real run land here — and `0 of 0` would read as an empty database. */
  | { readonly kind: 'starting' };

const RUNNING_STATES: ReadonlySet<string> = new Set(['pending', 'running']);

export function harvestActivity(summary: ConnectionSummary): HarvestActivity {
  if (summary.harvest_state === null || !RUNNING_STATES.has(summary.harvest_state)) {
    return { kind: 'idle' };
  }
  if (summary.discovered <= 0) return { kind: 'starting' };
  return { kind: 'running', observed: summary.observed, discovered: summary.discovered };
}

/** What a source's catalogue is worth reading, as one of five distinguishable states.
 *
 * **The interesting one is `staleAfterFailure`**, and it is the reason this is not a boolean. A
 * source can have a perfectly good catalogue from two hours ago *and* a failing credential since —
 * `last_harvest_at` and `harvest_state` are separate fields precisely so that pair is expressible —
 * and a screen that showed only one of them would either hide a broken credential or claim the
 * datasets on the next screen are unreachable when they are perfectly readable.
 *
 * `neverHarvested` is not `empty`: registering a source does not contact it, so a source with no
 * harvest has no catalogue rather than an empty one, and the next step differs (verify, then
 * harvest) from the next step for a source that was read and genuinely holds nothing.
 */
export type CatalogueState =
  | { readonly kind: 'neverHarvested' }
  | { readonly kind: 'harvesting' }
  | { readonly kind: 'current' }
  | { readonly kind: 'staleAfterFailure' }
  | { readonly kind: 'empty' };

export function catalogueState(summary: ConnectionSummary): CatalogueState {
  if (harvestActivity(summary).kind !== 'idle') return { kind: 'harvesting' };
  if (summary.last_harvest_at === null) return { kind: 'neverHarvested' };
  if (summary.datasets === 0) return { kind: 'empty' };
  /* A terminal state other than `succeeded` on the *most recent* run, with an earlier successful
   * one behind it. `failed` and `cancelled` both land here: in each case the rows on screen are real
   * and were read earlier, and nothing new has been read since. */
  if (summary.harvest_state !== null && summary.harvest_state !== 'succeeded') {
    return { kind: 'staleAfterFailure' };
  }
  return { kind: 'current' };
}

/** How much of a source has been scored, as a fraction to render — or why there is no fraction.
 *
 * Three outcomes, and collapsing any two of them puts a false statement on a card:
 *
 * * `notConsulted` — `scored` is null, so scoring was not asked. **Never rendered as 0**, which
 *   would claim an estate nobody has ever scored.
 * * `nothingToScore` — the source has no datasets, so "0 of 0" is arithmetically true and
 *   editorially useless: there is nothing here yet, which the catalogue state already says.
 * * `measured` — a real pair. `covered` can legitimately be 0, and that *is* a finding.
 */
export type ScoredCoverage =
  | { readonly kind: 'notConsulted' }
  | { readonly kind: 'nothingToScore' }
  | { readonly kind: 'measured'; readonly covered: number; readonly total: number };

export function scoredCoverage(summary: ConnectionSummary): ScoredCoverage {
  if (summary.scored === null) return { kind: 'notConsulted' };
  if (summary.datasets === 0) return { kind: 'nothingToScore' };
  return { kind: 'measured', covered: summary.scored, total: summary.datasets };
}

/** A dataset's profile figures, or the reason there are none.
 *
 * `facts` is `undefined` for a dataset the facts read never returned — which the API defines as
 * never profiled — and that is a different sentence from a profile that ran and measured an empty
 * table. Both are here because the screen has to say different things.
 *
 * `unread` covers the request itself failing. Without it a failed facts read would render as "never
 * profiled" on every row: a claim about the customer's data used to report a failure of ours, which
 * is the class this codebase records at four AI screens.
 */
export type ProfileReading =
  | { readonly kind: 'unread' }
  | { readonly kind: 'neverProfiled' }
  | { readonly kind: 'profiledEmpty'; readonly at: string }
  | { readonly kind: 'measured'; readonly rows: number; readonly at: string };

export function profileReading(
  facts: DatasetFacts | undefined,
  { unread }: { readonly unread: boolean },
): ProfileReading {
  if (unread) return { kind: 'unread' };
  if (facts === undefined) return { kind: 'neverProfiled' };
  if (facts.last_profiled_at === null) return { kind: 'neverProfiled' };
  if (facts.rows_at_last_profile === null) {
    return { kind: 'profiledEmpty', at: facts.last_profiled_at };
  }
  return {
    kind: 'measured',
    rows: facts.rows_at_last_profile,
    at: facts.last_profiled_at,
  };
}

/** Sources worth a reader's attention first.
 *
 * A source whose catalogue cannot be trusted, or which has never been read, is what somebody opening
 * this screen is looking for — so those sort ahead of the healthy ones. Within a band the order is
 * whatever the server gave (newest registered first), which keeps a freshly added source near the
 * top of its band rather than buried by name.
 *
 * **A pure sort over a copy**, so the caller's array is untouched: this runs inside a `useMemo` over
 * query data that React Query owns, and mutating it in place would reorder the cache.
 */
const ATTENTION_ORDER: readonly CatalogueState['kind'][] = [
  'staleAfterFailure',
  'neverHarvested',
  'harvesting',
  'empty',
  'current',
];

export function byAttention(
  summaries: readonly ConnectionSummary[],
): readonly ConnectionSummary[] {
  const rank = (summary: ConnectionSummary): number =>
    ATTENTION_ORDER.indexOf(catalogueState(summary).kind);
  /* A stable sort — `Array.prototype.sort` is required to be stable since ES2019 — so equal ranks
   * keep the server's order rather than depending on the engine. */
  return [...summaries].sort((left, right) => rank(left) - rank(right));
}

/** How many rule bindings appear to apply to a dataset, and whether that figure can be trusted.
 *
 * **A lower bound, and the type says so** rather than the caller remembering. `core.rules` owns
 * selector resolution and no route exposes "the bindings that resolve to this dataset", so the only
 * available answer matches `name_glob` selectors against the qualified name — see
 * `nameGlobMatches`, which this reuses rather than reimplements.
 *
 * That can only ever **under**-report: a binding reaching this dataset by tag or by column role is
 * missed. Under-reporting is the safe direction for a column headed "Rules" — a missing count sends
 * somebody to the Rules screen where the truth is, whereas an invented one would have them believe a
 * table is covered when it is not. `atLeast` is what stops the number being read as exact.
 *
 * `unread` is separate from a count of zero for the reason every absence in this file is: a failed
 * bindings read must not render as "no rules on this table", which is the most consequential wrong
 * answer this column can give.
 */
export type BindingCount =
  { readonly kind: 'unread' } | { readonly kind: 'atLeast'; readonly count: number };

export function bindingCount(
  bindings: readonly RuleBinding[] | undefined,
  qualifiedName: string,
  matches: (pattern: string, qualified: string) => boolean,
): BindingCount {
  if (bindings === undefined) return { kind: 'unread' };
  const count = bindings.filter((binding) =>
    binding.selectors.some(
      (selector) =>
        selector.kind === 'name_glob' &&
        /* `pattern` is optional on the wire — a `tag` selector carries none — so an absent one is
         * "matches nothing here" rather than a reason to throw. The same guard `DatasetDetail`'s
         * rules panel makes, and the reason this takes `matches` as an argument: one glob
         * implementation, shared, rather than a second idea of what `*` means. */
        typeof selector.pattern === 'string' &&
        matches(selector.pattern, qualifiedName),
    ),
  ).length;
  return { kind: 'atLeast', count };
}

/* The summary line under a dataset's name: how big, how good, how covered.
 *
 * **Clauses, not a template**, and that is the whole design. Each of the three facts comes from a
 * different request that can be absent, pending or failed independently, so a format string with
 * three holes would have to render something for each — and the something is always either a lie
 * ("0 rows") or noise ("— rows"). A caller joins whatever came back; a line of two clauses is
 * shorter and true, where a line of three with a placeholder is neither.
 *
 * Pure and separate from the component so the omission rules are testable without rendering: which
 * clauses appear for which combination of absences is the judgement here, and it is the kind that
 * gets quietly changed in JSX.
 */
export interface DatasetSummaryFacts {
  /** From the profile, so it is rows *scanned* and not the table's current count. */
  readonly reading: ProfileReading;
  /** `null` when no score exists, and `value: null` when one exists over no evidence. */
  readonly score: { readonly value: number | null } | null;
  readonly bindings: BindingCount;
}

export function datasetSummaryClauses(
  facts: DatasetSummaryFacts,
  words: {
    readonly rows: (count: number) => string;
    readonly scored: (value: string) => string;
    readonly unscored: string;
    readonly bound: (count: number) => string;
    readonly unbound: string;
  },
): readonly string[] {
  const clauses: string[] = [];

  /* Only a measured reading contributes. `neverProfiled`, `profiledEmpty` and `unread` each have a
   * sentence of their own inside the Profile tab, and none of them belongs in a summary line — "no
   * row count" is not a fact about the table worth the reader's first glance. */
  if (facts.reading.kind === 'measured') clauses.push(words.rows(facts.reading.rows));

  /* **A score of `null` is said, not omitted**, and that is the one asymmetry here. `scored 87.4`
   * and nothing are both readable; a *measured* absence — a score exists and no weighted dimension
   * held evidence — is a finding, and dropping the clause would hide it behind the same silence as
   * a dataset nobody has scored. */
  if (facts.score !== null) {
    clauses.push(
      facts.score.value === null
        ? words.unscored
        : words.scored(facts.score.value.toFixed(1)),
    );
  }

  /* A lower bound when there is one, and the words say so. Zero gets a clause too: "no rule names
   * this table" is exactly what a reader on this screen needs to know, and it is the state a
   * steward acts on. `unread` contributes nothing — a failed bindings read is about us. */
  if (facts.bindings.kind === 'atLeast') {
    clauses.push(
      facts.bindings.count === 0 ? words.unbound : words.bound(facts.bindings.count),
    );
  }

  return clauses;
}
